import { Box, CircularProgress, Divider, Grid2, Typography } from "@mui/material";
import ExpandAllButton from "./Components/ExpandAllButton";
import MapDigitalTwinSearch from "../Overview/components/MapDigitalTwinSearch";
import { PrimaryTooltip } from "../../Components/StyledComponent";
import { BatchActionIconButton } from "../../Features/Table/GenericDraggableTable/TableStyles";
import { DownloadFileIcon } from "../../Features/Table/Icons";
import EmailComposerDialog from "../../Features/Table/GenericDraggableTable/EmailComposerDialog";
import AlertListFiltersSelection from "./AlertListFiltersSelection";
import CustomCheckbox from "./Components/CustomCheckbox";
import AlertListSortBySelection from "./AlertListSortBySelection";
import { alertListAction as defaultAlertListAction, getBatteryAlertIncidents as defaultGetBatteryAlertIncidents } from "../../Api/BmsFaultsApi";
import DownloadCSV from "../../Helper/CSVOperations/DownloadCSV";
import { usePagesContent, useSearchableFields, searchFieldsKey } from "../../Hooks/usePagesContent";
import { csvColumns, csvColumnsForResolved } from "./AlertConfigs";
import { getDateRangeFromTimePeriod } from "./CommonFunctions";
import { useState } from "react";

const DEFAULT_SEARCHABLE_FIELDS = [
  {
    key: "licensePlateNo",
    title: "Bus Number",
  },
  {
    key: "deviceID",
    title: "device_id",
  },
  {
    key: "vin",
    title: "Chassis Number",
  },
];

const AlertListHeader = ({
  onExpandToggle,
  onSelectAllToggle,
  selectAll,
  resultCount,
  onSearch,
  selectedFilters,
  onApplyFilters,
  defaultSort,
  onSortChange,
  selectedTab,
  selectedBatteries,
  selectedAlertIncidents,
  searchTerm,
  globalFilters,
  selectedTimePeriod,
  incidentsCache,
  incidentFilters,
  // An explicit list (custom observability) wins; otherwise the org's
  // globalConfig.searchableFields, else DEFAULT_SEARCHABLE_FIELDS.
  searchableFields: searchableFieldsProp,
  showEmail = true,
  showDownload = true,
  actionFn = defaultAlertListAction,
  fetchIncidentsFn = defaultGetBatteryAlertIncidents,
  csvColumnsConfig = { active: csvColumns, resolved: csvColumnsForResolved },
  filenamePrefix = "Battery System Faults",
  buildCsvFn,
  filterConfigKey = "diagnosticsStateTable",
  alertStatusKey = "bmsAlertStatus",
}) => {
  const pageContent = usePagesContent();
  const orgSearchableFields = useSearchableFields(DEFAULT_SEARCHABLE_FIELDS);
  const searchableFields = searchableFieldsProp ?? orgSearchableFields;
  const [isDownloading, setIsDownloading] = useState(false);

  const prepareActionData = async () => {
    const dateRange = selectedTab === "resolved" ? getDateRangeFromTimePeriod(selectedTimePeriod) : null;
    const searchID = searchTerm.selectedSearchColumn || "searchID";
    const { activatedAt: selectedActivatedAt, ...restFilters } = selectedFilters;
    const filters = {
      ...(dateRange && dateRange),
      [searchID]: searchTerm.selectedDevices,
      errorStatus: [selectedTab === "resolved" ? "resolved" : "active"],
      ...restFilters,
      ...(selectedActivatedAt ? { activatedAt: Array.isArray(selectedActivatedAt) ? selectedActivatedAt[0] : selectedActivatedAt } : {}),
    };

    // If selectAll, no need to fetch individual incident IDs
    if (selectAll) {
      return {
        selectAll: true,
        resourceIDs: [],
        filters: JSON.stringify(filters),
      };
    }

    // Collect incident IDs for selected batteries
    const allIncidentIds = [];
    const batteriesToFetch = [];

    // Check cache first for each selected battery
    for (const batteryID of selectedBatteries) {
      if (incidentsCache[batteryID]) {
        // Use cached incidents
        incidentsCache[batteryID].forEach(incident => {
          allIncidentIds.push(incident.incidentID);
        });
      } else {
        // Need to fetch for this battery
        batteriesToFetch.push(batteryID);
      }
    }

    // Fetch incidents for batteries not in cache
    if (batteriesToFetch.length > 0) {
      const fetchPromises = batteriesToFetch.map(async (batteryID) => {
        try {
          const incidentsRes = await fetchIncidentsFn(
            globalFilters,
            batteryID,
            incidentFilters
          );
          return incidentsRes?.response?.data || [];
        } catch (error) {
          // ignore — battery silently excluded from batch if fetch fails
          return [];
        }
      });

      const fetchedResults = await Promise.all(fetchPromises);
      fetchedResults.forEach(incidents => {
        incidents.forEach(incident => {
          allIncidentIds.push(incident.incidentID);
        });
      });
    }

    return {
      resourceIDs: allIncidentIds,
      filters: JSON.stringify(filters),
    };
  };

  const handleDownloadCsv = async () => {
    if (isDownloading) return;
    setIsDownloading(true);
    try {
      const actionData = await prepareActionData();
      const columns = selectedTab === "resolved" ? csvColumnsConfig.resolved : csvColumnsConfig.active;
      const res = await actionFn(globalFilters, {
        ...actionData,
        actionType: "download-csv",
        batteryID: Array.from(selectedBatteries),
        columnOrder: columns.map(column => column.headerLabel),
        columns: JSON.stringify(columns),
      });
      const csvData = res?.response?.data || [];
      if (buildCsvFn) {
        buildCsvFn(csvData, filenamePrefix);
      } else {
        DownloadCSV(filenamePrefix, csvData);
      }
    } finally {
      setIsDownloading(false);
    }
  };

  const handleEmailCsv = async ({ sendTo, sendCc, subject, body }) => {
    const actionData = await prepareActionData();
    const columns = selectedTab === "resolved" ? csvColumnsConfig.resolved : csvColumnsConfig.active;
    await actionFn(globalFilters, {
      ...actionData,
      batteryID: Array.from(selectedBatteries),
      columnOrder: columns.map(column => column.headerLabel),
      columns: JSON.stringify(columns),
      emailTo: sendTo,
      emailCc: sendCc,
      subject: subject,
      body: body,
      actionType: "mail",
    });
  };

  return (
    <Grid2 container flexDirection={"column"} gap={2.5}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <MapDigitalTwinSearch
          key={searchFieldsKey(searchableFields)}
          onSearch={onSearch}
          searchableFields={searchableFields}
          containerWidth={"40%"}
        />
        <Grid2 container gap={1} alignItems={"center"}>
          {(selectedBatteries.size || selectAll) && (
            <Typography variant="fw600s14lh16c6D72F6">
              {selectAll ? resultCount : selectedBatteries.size} Selected
            </Typography>
          )}
          {showDownload && (
            <PrimaryTooltip
              title={
                isDownloading ? (
                  <>Downloading...</>
                ) : selectedBatteries.size > 0 ? (
                  <>Download Data</>
                ) : (
                  <>Download Data <br /> (Select minimum 1 row)</>
                )
              }
              placement="bottom"
            >
              <BatchActionIconButton
                onClick={handleDownloadCsv}
                active={selectedBatteries.size > 0 && !isDownloading}
                disabled={selectedBatteries.size === 0 || isDownloading}
              >
                {isDownloading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <DownloadFileIcon fontSize="inherit" />
                )}
              </BatchActionIconButton>
            </PrimaryTooltip>
          )}
          {showEmail && (
            <EmailComposerDialog
              availableEmailDomains={
                pageContent?.metaInfo?.globalConfig?.["email.domains"] || []
              }
              emailInfo={{}}
              getCSVData={() => {}}
              onDownloadCSV={handleDownloadCsv}
              selectedRows={Array.from(selectedBatteries)}
              active={selectedBatteries.size > 0}
              onSendEmail={handleEmailCsv}
            />
          )}
        </Grid2>
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          bgcolor: "#F1F1F1",
          borderRadius: "10px",
          py: 0.75,
          px: 2,
        }}
      >
        <Grid2 container gap={1.5} alignItems={"center"}>
          <PrimaryTooltip title={"Select All"} placement="bottom">
            <CustomCheckbox
              checked={selectAll}
              onChange={() => onSelectAllToggle(!selectAll)}
            />
          </PrimaryTooltip>
          <Divider
            orientation="vertical"
            flexItem
            sx={{ borderColor: "#E4E4E4", my: 1 }}
          />
          <AlertListSortBySelection onSortChange={onSortChange} defaultSort={defaultSort} />
          <Divider
            orientation="vertical"
            flexItem
            sx={{ borderColor: "#E4E4E4", my: 1 }}
          />
          <AlertListFiltersSelection
            selectedFilters={selectedFilters}
            onApplyFilters={onApplyFilters}
            selectedTab={selectedTab}
            filterConfigKey={filterConfigKey}
            alertStatusKey={alertStatusKey}
          />
        </Grid2>
        <Grid2 container gap={1} alignItems={"center"}>
          <Box>
            <Typography variant="fw500s12lh14c3D4B5A">
              {resultCount} {resultCount === 1 ? "Vehicle" : "Vehicles"}
            </Typography>
          </Box>
          <Divider
            orientation="vertical"
            flexItem
            sx={{ borderColor: "#E4E4E4" }}
          />
          <ExpandAllButton
            selectedTab={selectedTab}
            onToggle={onExpandToggle}
          />
        </Grid2>
      </Box>
    </Grid2>
  );
};

export default AlertListHeader;
